import { useAuth } from "@clerk/clerk-react";
import { Divider, Grid, Typography } from "@mui/material";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { OrganizationUser } from "schemas/dashboard";
import { SetAlertType } from "schemas/functions";

import { fetcherAuth, isNotFoundError } from "utils/api";

import AdminAffiliateOrganization from "../Affiliate";
import AdminPersonalizedPlan from "../PersonalizedPlan";
import Settings from "../Settings";
import AdminAddOns from "../Settings/AddOns";
import AdminSearchThreads from "../Threads";
import AdminUserSearchPreferences from "../UserSearchPreferences";

interface AdminOrganizationViewProps {
  setAlert: SetAlertType;
  organizationUser: OrganizationUser | null;
  setOrganizationUser: Dispatch<SetStateAction<OrganizationUser | null>>;
  setOrgUsers: Dispatch<SetStateAction<OrganizationUser[]>>;
}

export default function AdminOrganizationView({
  setAlert,
  organizationUser,
  setOrganizationUser,
  setOrgUsers,
}: AdminOrganizationViewProps) {
  const { id } = useParams();
  const { getToken } = useAuth();
  const [orgExists, setOrgExists] = useState<boolean | null>(null);

  useEffect(() => {
    if (!id) return;
    setOrgExists(null);
    const checkOrgExists = async () => {
      try {
        await fetcherAuth(
          getToken,
          `/api/admin/organization-profiles/${id}`,
          "GET",
        );
        setOrgExists(true);
      } catch (error) {
        setOrgExists(isNotFoundError(error) ? false : true);
      }
    };
    checkOrgExists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!id || orgExists === null) return <></>;

  if (!orgExists) {
    return (
      <Typography variant="h4" sx={{ mt: 3 }}>
        Organization {id} does not exist
      </Typography>
    );
  }

  return (
    <Grid>
      <Typography variant="h4" sx={{ mt: 3 }}>
        {organizationUser
          ? `${organizationUser.name} - ${organizationUser.email}`
          : `Organization ${id}`}
      </Typography>
      <Settings
        organizationId={Number(id)}
        setAlert={setAlert}
        organizationUser={organizationUser}
        setOrganizationUser={setOrganizationUser}
        setOrgUsers={setOrgUsers}
      />

      <Divider sx={{ my: 3 }} />
      <AdminUserSearchPreferences
        organizationId={Number(id)}
        setAlert={setAlert}
      />

      <Divider sx={{ my: 3 }} />
      <AdminPersonalizedPlan organizationId={Number(id)} setAlert={setAlert} />

      <Divider sx={{ my: 3 }} />
      <AdminAddOns organizationUser={organizationUser} setAlert={setAlert} />
      <Divider sx={{ my: 3 }} />
      <AdminAffiliateOrganization
        organizationUser={organizationUser}
        setAlert={setAlert}
      />
      <Divider sx={{ my: 3 }} />
      <Typography variant="h4" sx={{ my: 2 }}>
        <strong>View Emails</strong>
      </Typography>
      <AdminSearchThreads organizationId={Number(id)} setAlert={setAlert} />
    </Grid>
  );
}
